import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import * as qs from 'qs';
import { TrackJS } from 'trackjs';
import ErrorRedirectService from '../../routes/error/errorRedirect.service';
import store from '../../utils/store';
import { HttpStatus } from '../httpStatus.constant';
import { OauthAPIEndpoints } from './oauthAPIEndpoints.constant';

type OauthAPIMethod = 'GET' | 'PUT' | 'POST';

class OauthAPI {
    public checkAccessTokenPresence(): string {
        const accessToken = webStorage.getAccessToken();

        if (!accessToken) {
            ErrorRedirectService.notLoggedIn();
            return '';
        }

        return accessToken;
    }

    public async checkSessionValidity(operation: string): Promise<boolean | void | any> {
        const endpoint = OauthAPIEndpoints.INTROSPECT;
        const accessToken = this.checkAccessTokenPresence();
        const requestInit = this.getRequestInit('POST', {
            token: accessToken,
            operation: operation,
        });
        const endpointUrl = this.getEndpointUrl(endpoint);

        try {
            const fetchResponse = await fetch(endpointUrl, requestInit);
            const { ok, status } = fetchResponse;

            if (!ok) {
                if (status != HttpStatus.PRECONDITION_FAILED) {
                    this.handleTokenValidationErrors(status);
                } else {
                    const json = await fetchResponse.json();
                    ErrorRedirectService.authError(json.r10Rejection, operation);
                }
            } else {
                const json = await fetchResponse.json();
                const { status } = json;
                if (status === 'valid') {
                    return true;
                } else {
                    this.handleTokenValidationErrors(403);
                }
            }
        } catch {
            ErrorRedirectService.unavailable();
        }
    }

    public async checkPasswordValidity(password: string): Promise<boolean | void> {
        const endpoint = OauthAPIEndpoints.VALIDATE;
        const accessToken = webStorage.getAccessToken();
        const requestInit = this.getRequestInit('POST', { myrToken: accessToken, password });
        const endpointUrl = this.getEndpointUrl(endpoint);

        try {
            const response = await fetch(endpointUrl, requestInit);
            if (!response.ok) {
                this.handleTokenValidationErrors(response.status);
            } else {
                const { valid } = await response.json();
                return valid;
            }
        } catch (error) {
            ErrorRedirectService.unavailable();
        }
    }

    public async updateAccessToken(): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const accessToken = this.checkAccessTokenPresence();
            const appConfigEmpty = Object.keys(store.getState().app.appConfig).length === 0;
            if (appConfigEmpty) {
                TrackJS.track('Trying to call update without app config');
                ErrorRedirectService.forbidden();
                reject();
            } else {
                await this.getUpdatedAccessToken(accessToken);
                resolve();
            }
        });
    }

    public getUpdatedAccessToken(accessToken: string): Promise<void> {
        const endpoint = OauthAPIEndpoints.UPDATE;
        const requestInit = this.getRequestInit('POST', { token: accessToken });
        const endpointUrl = this.getEndpointUrl(endpoint);

        if (!requestInit || !endpointUrl) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            fetch(endpointUrl, requestInit)
                .then((response) => {
                    const { ok, status } = response;
                    if (!ok) {
                        this.handleTokenValidationErrors(status);
                        reject();
                    }
                    return response.json();
                })
                .then((json) => {
                    const { token } = json;
                    webStorage.setAccessToken(token);
                    resolve();
                })
                .catch(() => {
                    ErrorRedirectService.unavailable();
                    reject();
                });
        });
    }

    private getEndpointUrl(endpoint: string, contentBody?: any): string {
        const BASE_URL = store.getState().app.appConfig.BASE_OAUTH_API_URL;
        const endpointUrl = new URL(`${BASE_URL}${endpoint}`);
        const searchParams = contentBody ? new URLSearchParams(qs.stringify(contentBody)) : '';

        if (!endpointUrl || (contentBody && !searchParams)) {
            TrackJS.track('Invalid oauth endpoint url');
            ErrorRedirectService.unexpected();
            return '';
        }

        if (contentBody) {
            endpointUrl.search = searchParams.toString();
        }

        return endpointUrl.toString();
    }

    private getRequestHeaders(method: OauthAPIMethod): HeadersInit {
        const headers = new Headers();

        if (method === 'POST' || method === 'PUT') {
            headers.set('Content-Type', 'application/json; charset=UTF-8');
        }

        return headers;
    }

    private getRequestInit(
        method: OauthAPIMethod,
        contentBody?: any,
        signal?: AbortSignal,
    ): RequestInit {
        const requestInit = {} as RequestInit;

        requestInit.method = method;

        requestInit.headers = this.getRequestHeaders(method);

        if (signal) {
            requestInit.signal = signal;
        }

        if (contentBody) {
            requestInit.body = JSON.stringify(contentBody);
        }

        return requestInit;
    }

    private handleTokenValidationErrors(status: number): void {
        TrackJS.track(`OAuth API call to introspect returned status ${status}`);

        switch (status) {
            case HttpStatus.UNAUTHORIZED:
            case HttpStatus.FORBIDDEN:
                ErrorRedirectService.notLoggedIn();
                break;
            case HttpStatus.BAD_REQUEST:
            case HttpStatus.UNPROCESSABLE_ENTITY:
                ErrorRedirectService.notLoggedIn();
                break;
            case HttpStatus.INTERNAL_SERVER_ERROR:
                ErrorRedirectService.unexpected();
                break;
            case HttpStatus.BAD_GATEWAY:
            case HttpStatus.SERVICE_UNAVAILABLE:
                // Currently there is a bug in load balancer config that
                // might cause in 502 & 503 issues happen more often than they should
                ErrorRedirectService.unavailable();
                break;
            default:
                ErrorRedirectService.unexpected();
        }
    }
}

export const oauthAPIService = new OauthAPI();
